import { useCallback, useMemo, useRef } from "react";
import type { VisibilityState } from "@salestrak/ui";

import {
  layerColumnVisibility,
  splitColumnVisibility,
  useTableViewSettings,
  type TableViewSettingsPanelProps,
  type TableViewTableProps,
} from "#app/lib/hooks/use-table-view-settings";
import { EMPTY_VIEW_COLUMNS, type ViewColumns } from "#app/lib/view-config";

/**
 * useViewColumnState — column order/visibility for a table that renders both
 * inside and outside a saved entity view.
 *
 * Inside a saved view (`activeViewId` set) the column set belongs to the
 * VIEW: the Customize view panel's Fields page, the table's header "+" and
 * in-table column drags all edit the view draft through `onColumnsChange`,
 * so they raise Save and land for everyone who uses the view. Widths stay
 * per-user either way — a reading ergonomic, not a view decision — persisted
 * under `<tableId>:<viewId>` so each view keeps its own widths. Outside a
 * saved view the per-user `useTableViewSettings` pool owns everything, as
 * there is no view row to draft against.
 *
 * While the views scope is still resolving (`viewsLoading`) the table paints
 * its defaults with inert handlers: the unscoped per-user pool belongs to
 * the viewless screens and would flash a different layout before the view's
 * own columns snap in, and an edit made in that window has no owner.
 */

type ViewColumnState = {
  tableProps: TableViewTableProps;
  settingsProps: TableViewSettingsPanelProps;
};

const NO_DEFAULT_HIDDEN: string[] = [];
const EMPTY_SIZING: Record<string, number> = {};
const noop = () => {};

export function useViewColumnState(options: {
  /** Per-user settings key outside a view; widths persist under
   *  `<tableId>:<viewId>` inside one. */
  tableId: string;
  /** Columns hidden until shown explicitly. Pass a memoized array. */
  defaultHiddenColumnIds?: string[];
  /** The active saved view, or null when the per-user pool owns the state. */
  activeViewId: string | null;
  /** True while the views scope is resolving which view is active; false on
   *  screens with no views scope at all. */
  viewsLoading: boolean;
  /** The view draft's column state (`config.columns`). */
  columns: ViewColumns;
  /** Writes the draft — `(columns) => setConfig({ columns })`. */
  onColumnsChange: (columns: ViewColumns) => void;
}): ViewColumnState {
  const {
    tableId,
    defaultHiddenColumnIds,
    activeViewId,
    viewsLoading,
    columns,
    onColumnsChange,
  } = options;
  const defaultHiddenIds = defaultHiddenColumnIds ?? NO_DEFAULT_HIDDEN;

  const tableView = useTableViewSettings(
    activeViewId ? `${tableId}:${activeViewId}` : tableId,
    { defaultHiddenColumnIds: defaultHiddenIds },
  );

  // The handlers read the latest draft through a ref so their identities —
  // and everything memoized on them — survive each edit. Each write ALSO
  // updates the ref eagerly: a single gesture can emit an order write and a
  // visibility write in the same tick (showing a column appends it to the
  // order), and a render-only ref would hand the second write a stale draft
  // that silently reverts the first.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const commitColumns = useCallback(
    (next: ViewColumns) => {
      columnsRef.current = next;
      onColumnsChange(next);
    },
    [onColumnsChange],
  );

  const viewVisibility = useMemo(
    () => layerColumnVisibility(defaultHiddenIds, columns.shown, columns.hidden),
    [defaultHiddenIds, columns.shown, columns.hidden],
  );

  const handleOrderChange = useCallback(
    (order: string[]) => commitColumns({ ...columnsRef.current, order }),
    [commitColumns],
  );
  const handleVisibilityChange = useCallback(
    (visibility: VisibilityState) => {
      const prev = columnsRef.current;
      const { hidden, shown } = splitColumnVisibility(visibility, {
        defaultHiddenIds,
        prevShown: prev.shown,
      });
      commitColumns({ ...prev, hidden, shown });
    },
    [defaultHiddenIds, commitColumns],
  );
  const handleReset = useCallback(
    () => commitColumns(EMPTY_VIEW_COLUMNS),
    [commitColumns],
  );

  return useMemo<ViewColumnState>(() => {
    if (!viewsLoading && activeViewId === null) return tableView;
    const settled = !viewsLoading;
    return {
      tableProps: {
        columnOrder: columns.order.length > 0 ? columns.order : undefined,
        onColumnOrderChange: settled ? handleOrderChange : noop,
        columnVisibility: viewVisibility,
        columnSizing: settled ? tableView.tableProps.columnSizing : EMPTY_SIZING,
        onColumnSizingChange: settled
          ? tableView.tableProps.onColumnSizingChange
          : noop,
      },
      settingsProps: {
        columnOrder: columns.order,
        columnVisibility: viewVisibility,
        onColumnOrderChange: settled ? handleOrderChange : noop,
        onColumnVisibilityChange: settled ? handleVisibilityChange : noop,
        onReset: settled ? handleReset : noop,
      },
    };
  }, [
    viewsLoading,
    activeViewId,
    tableView,
    columns.order,
    viewVisibility,
    handleOrderChange,
    handleVisibilityChange,
    handleReset,
  ]);
}
